import {
  Center,
  Flex,
  FlexProps,
  SimpleGrid,
  Spinner,
  Text,
} from "@chakra-ui/react";
import axios from "axios";
import { useTranslation } from "next-i18next";
import React, { useCallback } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import {
  DndContext,
  useSensor,
  useSensors,
  PointerSensor,
  DragEndEvent,
} from "@dnd-kit/core";

import BoxCard from "@/components/common/BoxCard";
import { useToastPromise } from "@/hooks/useToast";
import { File, Folder } from "@/types/cloud";
import { FileCard, FileRow } from "./File";
import { FolderCard, FolderRow } from "./Folder";

type Props = {
  data: {
    files: File[];
    folders: Folder[];
    hasNextPage?: boolean;
    error?: any;
    isLoading?: boolean;
    loadMore?: () => void;
    refresh?: () => void;
  };
  isSearching?: boolean;
  onUpdate?: () => void;
  onPreviewFile?: (file: File) => void;
  onEditFile?: (file: File) => void;
  onFolderClick?: (slug: string, path?: string, id?: number) => void;
  onSelectTag?: (id: number) => void;
  preview?: boolean;
  onFolderCheck?: (
    e: React.ChangeEvent<HTMLInputElement>,
    folder: Folder
  ) => void;
  onFileCheck?: (e: React.ChangeEvent<HTMLInputElement>, file: File) => void;
  checkedFoldersIds?: number[];
  checkedFilesIds?: number[];
};

const CloudGrid = ({
  data,
  isSearching,
  onUpdate,
  onEditFile,
  onFolderClick,
  onPreviewFile,
  onSelectTag,
  preview = false,
  onFolderCheck,
  onFileCheck,
  checkedFoldersIds,
  checkedFilesIds,
  ...rest
}: Props & FlexProps): React.ReactElement => {
  const { t } = useTranslation("common");
  const toast = useToastPromise();

  const onFolderDelete = useCallback(
    (id) =>
      toast.promise(axios.delete(`/cloud/folder/${id}`).then(() => onUpdate())),
    [onUpdate]
  );
  const onFileDelete = useCallback(
    (id) =>
      toast.promise(axios.delete(`/cloud/file/${id}`).then(() => onUpdate())),
    [onUpdate]
  );

  const [sentryRef] = useInfiniteScroll({
    hasNextPage: data?.hasNextPage,
    disabled: !!data?.error,
    loading: data?.isLoading,
    onLoadMore: data?.loadMore,
    rootMargin: "0px 0px 400px 0px",
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const onDragEnd = (dragEndEvent: DragEndEvent) => {
    if (dragEndEvent?.over?.data?.current?.id) {
      const dataToSend = {
        parentFolderId: dragEndEvent?.over?.data?.current?.id,
        folderId: null,
        fileId: null,
      };

      if (dragEndEvent?.active?.data?.current?.slug) {
        if (
          dragEndEvent?.active?.data?.current?.id === dataToSend?.parentFolderId
        )
          return;

        dataToSend.folderId = dragEndEvent?.active?.data?.current?.id;
      }

      if (dragEndEvent?.active?.data?.current?.filename)
        dataToSend.fileId = dragEndEvent?.active?.data?.current?.id;

      toast.promise(
        axios.post("/cloud/move", dataToSend).then(() => {
          if (data?.refresh) data?.refresh();
        }),
        {
          success: t(
            `cloud.DnD.success${
              dragEndEvent?.active?.data?.current?.slug ? "Folder" : "File"
            }`
          ),
          pending: t(
            `cloud.DnD.pending${
              dragEndEvent?.active?.data?.current?.slug ? "Folder" : "File"
            }`
          ),
          error: t(
            `cloud.DnD.error${
              dragEndEvent?.active?.data?.current?.slug ? "Folder" : "File"
            }`
          ),
        }
      );
    }
  };

  if (isSearching) {
    return (
      <SimpleGrid
        columns={1}
        spacing={6}
        flex={{ base: "none", md: 1 }}
        w={{ base: "100%", md: "auto" }}
        overflowX="auto"
      >
        <BoxCard
          px={0}
          py={1}
          textAlign="center"
          minW={{ base: "700px", md: 0 }}
        >
          {!data?.folders?.length && data?.files?.length === 0 && (
            <Text fontWeight="500" py={2}>
              {t("noResultsFound")}
            </Text>
          )}
          {data?.folders?.map((folder) => (
            <FolderRow
              minH="200px"
              key={`folder-${folder?.id}`}
              folder={folder}
              filesCount={folder?.files?.length || 0}
              onClick={onFolderClick}
              onDelete={onFolderDelete}
            />
          ))}
          {data?.files?.map((file) => (
            <FileRow
              key={`files-${file?.id}`}
              file={file}
              onDelete={onFileDelete}
              onClick={() => onPreviewFile(file)}
              onEdit={() => onEditFile(file)}
              onSelectTag={onSelectTag}
            />
          ))}
          {(data?.isLoading || data?.hasNextPage) && (
            <Center ref={sentryRef} mt="2">
              <Spinner />
            </Center>
          )}
        </BoxCard>
      </SimpleGrid>
    );
  }

  const GridContent = (): React.ReactElement => (
    <SimpleGrid
      columns={{ base: 2, md: 4 }}
      spacing={6}
      flex={{ base: "none", md: 1 }}
      w={{ base: "100%", md: "auto" }}
    >
      {data?.folders?.map((folder) => (
        <FolderCard
          key={`folder-${folder?.id}`}
          folder={folder}
          filesCount={folder?.files?.length || 0}
          onClick={onFolderClick}
          onDelete={onFolderDelete}
          hideDetails={preview}
          hideCheckbox={!onFolderCheck}
          onCheckClick={onFolderCheck}
          isChecked={checkedFoldersIds?.includes(folder?.id)}
        />
      ))}
      {data?.files?.map((file) => (
        <FileCard
          key={`files-${file?.id}`}
          file={file}
          onDelete={onFileDelete}
          onClick={() => onPreviewFile(file)}
          onEdit={() => onEditFile(file)}
          onSelectTag={onSelectTag}
          hideDetails={preview}
          preview={preview}
          onCheckClick={onFileCheck}
          hideCheckbox={!onFileCheck}
          isChecked={checkedFilesIds?.includes(file?.id)}
        />
      ))}
    </SimpleGrid>
  );

  return (
    <Flex flexDirection="column" gap={4} {...rest}>
      {preview ? (
        <GridContent />
      ) : (
        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <GridContent />
        </DndContext>
      )}

      {(data?.isLoading || data?.hasNextPage) && (
        <Center ref={sentryRef}>
          <Spinner />
        </Center>
      )}
    </Flex>
  );
};

export default CloudGrid;
